import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    Database,
    Server,
    Wrench,
    AlertTriangle,
    CheckCircle2,
    Trash2,
    Loader2,
    Archive,
    Layers,
    PlayCircle,
    StopCircle,
    Eye,
    Save,
    Upload,
} from "lucide-react";

export default function DatabaseAdmin() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [working, setWorking] = useState("");
    const [stats, setStats] = useState({});         // company_id → stats
    const [statsLoading, setStatsLoading] = useState({});
    const [report, setReport] = useState(null);
    const [backups, setBackups] = useState([]);
    const [confirmEnableMigration, setConfirmEnableMigration] = useState(false);
    const [activeJob, setActiveJob] = useState(null);

    // Poll an in-flight migration job
    useEffect(() => {
        if (!activeJob || activeJob.status === "complete" || activeJob.status === "failed") return;
        const t = setInterval(async () => {
            try {
                const { data } = await api.get(`/super-admin/database/jobs/${activeJob.id}`);
                setActiveJob(data);
                if (data.status === "complete" || data.status === "failed") {
                    clearInterval(t);
                    if (data.status === "complete") {
                        setReport(data.report);
                        toast.success(`Migration complete — ${data.report?.total_docs_copied || 0} docs copied, ${data.report?.total_skipped || 0} skipped.`);
                    } else {
                        toast.error(data.error || "Migration failed");
                    }
                    await load();
                }
            } catch {/* keep polling */}
        }, 1500);
        return () => clearInterval(t);
    }, [activeJob]);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/super-admin/database/status");
            setStatus(data);
            const { data: bk } = await api.get("/super-admin/database/backups");
            setBackups(bk?.backups || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const fetchStats = async (cid) => {
        setStatsLoading((p) => ({ ...p, [cid]: true }));
        try {
            const { data } = await api.get(`/super-admin/database/tenant/${cid}`);
            setStats((p) => ({ ...p, [cid]: data }));
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setStatsLoading((p) => ({ ...p, [cid]: false }));
        }
    };

    const enableMigration = async () => {
        setWorking("enable");
        try {
            // Kick off as a background job — returns immediately
            const { data } = await api.post("/super-admin/database/enable-per-tenant-async");
            setActiveJob({ id: data.job_id, status: data.status, queued_at: new Date().toISOString() });
            setConfirmEnableMigration(false);
            toast.message("Migration kicked off — watching progress…");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setWorking("");
        }
    };

    const [confirmCleanup, setConfirmCleanup] = useState(false);
    const cleanupLegacy = async () => {
        setWorking("cleanup");
        try {
            const { data } = await api.post("/super-admin/database/cleanup-legacy");
            setReport(data);
            setConfirmCleanup(false);
            toast.success(`Dropped ${data.total_collections_dropped} legacy collections from central DB.`);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setWorking("");
        }
    };

    const disableMode = async () => {
        if (!window.confirm("Switch back to legacy shared-DB mode? Per-tenant DBs stay on disk but will be ignored.")) return;
        setWorking("disable");
        try {
            await api.post("/super-admin/database/disable-per-tenant");
            toast.success("Per-tenant DB mode disabled.");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setWorking("");
        }
    };

    const rewrite = async (cid, { dryRun }) => {
        const verb = dryRun ? "Preview repair" : "Rewrite database";
        if (!dryRun && !window.confirm(`${verb} for "${cid}"?\n\nA full backup is taken first. Orphan rows referencing deleted employees/runs will be removed. Indexes will be rebuilt.\n\nContinue?`)) return;
        setWorking(`rewrite-${cid}`);
        try {
            const { data } = await api.post("/super-admin/database/rewrite", {
                company_id: cid,
                fix_orphans: true,
                dry_run: !!dryRun,
            });
            setReport(data);
            toast.success(dryRun ? "Dry-run complete. See report below." : `Rewrite complete for ${cid}.`);
            await load();
            await fetchStats(cid);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setWorking("");
        }
    };

    const dropDb = async (name) => {
        if (!window.confirm(`Permanently delete database "${name}"? This cannot be undone.`)) return;
        setWorking(`drop-${name}`);
        try {
            await api.post("/super-admin/database/drop-database", { db_name: name });
            toast.success(`Dropped ${name}.`);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setWorking("");
        }
    };

    const initTenant = async (cid) => {
        if (!window.confirm(
            `Initialize the missing tenant database for "${cid}"?\n\n` +
            `This creates "hrm_${cid.toLowerCase()}" on the Mongo server, builds the standard indexes, and seeds default company_settings + departments. Idempotent — safe to re-run.`,
        )) return;
        setWorking(`init-${cid}`);
        try {
            const { data } = await api.post(`/super-admin/database/init-tenant/${cid}`);
            setReport(data);
            if (data.ok) {
                toast.success(`hrm_${cid.toLowerCase()} initialized (created: ${(data.created || []).join(", ") || "—"}).`);
            } else {
                toast.error(`Init partially failed. Check the report panel — common cause is the production Mongo user lacking write access on new databases.`);
            }
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setWorking("");
        }
    };

    const backup = async (cid) => {
        if (!window.confirm(`Create a backup snapshot of "${cid}"?\n\nA non-destructive copy will be saved as hrm_${cid.toLowerCase()}_bak_<timestamp>. The source DB is untouched.`)) return;
        setWorking(`backup-${cid}`);
        try {
            const { data } = await api.post("/super-admin/database/backup", { company_id: cid });
            setReport(data);
            toast.success(`Backup complete · ${data.total_docs_backed_up} docs → ${data.backup_database}`);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setWorking("");
        }
    };

    const restore = async (cid) => {
        const cidBackups = backups.filter((b) => b.startsWith(`hrm_${cid.toLowerCase()}_bak_`));
        if (cidBackups.length === 0) {
            toast.error(`No backups available for "${cid}". Create one with the Backup button first.`);
            return;
        }
        const choice = window.prompt(
            `Restore "${cid}" from which backup?\n\nAvailable:\n${cidBackups.map((b, i) => `${i + 1}. ${b}`).join("\n")}\n\nEnter the number (1-${cidBackups.length}), or paste the full backup database name:`,
            String(cidBackups.length),
        );
        if (!choice) return;
        let backupName = "";
        const n = parseInt(choice, 10);
        if (!isNaN(n) && n >= 1 && n <= cidBackups.length) {
            backupName = cidBackups[n - 1];
        } else if (cidBackups.includes(choice.trim())) {
            backupName = choice.trim();
        } else {
            toast.error("Invalid selection.");
            return;
        }
        if (!window.confirm(
            `Restore "${cid}" from ${backupName}?\n\n` +
            `⚠️ This WIPES the tenant's current data and replaces it with the snapshot. ` +
            `If you want a partial merge instead, cancel and use the API with wipe_first=false.\n\nContinue?`,
        )) return;
        setWorking(`restore-${cid}`);
        try {
            const { data } = await api.post("/super-admin/database/restore", {
                company_id: cid,
                backup_db_name: backupName,
                wipe_first: true,
            });
            setReport(data);
            toast.success(`Restore complete · ${data.total_docs_restored} docs from ${backupName}`);
            await load();
            await fetchStats(cid);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setWorking("");
        }
    };

    if (loading) {
        return <div className="px-6 md:px-12 py-12 text-sm text-ink/40">Loading database status…</div>;
    }

    const perTenant = !!status?.per_tenant_db_mode;

    return (
        <div className="px-6 md:px-12 py-10 md:py-14" data-testid="db-admin-page">
            <div>
                <div className="overline">Platform operations</div>
                <h1 className="mt-3 font-display font-black tracking-tighter text-3xl sm:text-4xl text-ink leading-[1.02] flex items-center gap-3">
                    <Database className="w-8 h-8 text-klein" strokeWidth={1.5} /> Database administration
                </h1>
                <p className="mt-3 text-sm text-ink/60 max-w-2xl">
                    Manage tenant isolation, inspect collection counts, repair corrupt data, and rotate backups. All actions are scoped to the platform owner.
                </p>
            </div>

            {/* Mode banner */}
            <div
                className={`mt-8 border p-5 ${perTenant ? "border-emerald-200 bg-emerald-50" : "border-yellow-200 bg-yellow-50"}`}
                data-testid="db-admin-mode-banner"
            >
                <div className="flex items-start gap-3">
                    <Layers className={`w-5 h-5 ${perTenant ? "text-emerald-700" : "text-yellow-700"}`} />
                    <div className="flex-1">
                        <div className="font-display font-bold text-lg text-ink">
                            {perTenant ? "Per-tenant database mode (recommended)" : "Legacy shared-database mode"}
                        </div>
                        <div className="text-sm text-ink/70 mt-1">
                            {perTenant
                                ? <>Each tenant lives in its own physical Mongo database (<code className="font-mono text-xs">{status.central_db}/hrm_&lt;company-id&gt;</code>). Cross-tenant duplicates (e.g. same employee email at different companies) are allowed.</>
                                : <>All tenants share one database with <code className="font-mono text-xs">company_id</code> on every doc. Cross-tenant duplicates are blocked by global indexes.</>
                            }
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        {perTenant ? (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setConfirmCleanup(true)}
                                    disabled={!!working}
                                    data-testid="db-admin-cleanup-legacy"
                                    className="inline-flex items-center gap-1 border border-red-300 text-red-700 px-3 py-2 text-xs hover:bg-red-50"
                                >
                                    {working === "cleanup" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                                    Drop legacy data from central
                                </button>
                                <button
                                    onClick={disableMode}
                                    disabled={!!working}
                                    data-testid="db-admin-disable-pt"
                                    className="inline-flex items-center gap-1 border border-yellow-400 text-yellow-900 px-3 py-2 text-xs hover:bg-yellow-100"
                                >
                                    {working === "disable" ? <Loader2 className="w-3 h-3 animate-spin" /> : <StopCircle className="w-3 h-3" />}
                                    Disable per-tenant mode
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={() => setConfirmEnableMigration(true)}
                                disabled={!!working}
                                data-testid="db-admin-enable-pt"
                                className="inline-flex items-center gap-1 bg-klein text-white px-4 py-2 text-sm hover:bg-klein/90"
                            >
                                {working === "enable" ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
                                Migrate to per-tenant DBs
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Tenants */}
            <div className="mt-10">
                <h2 className="font-display font-bold text-xl tracking-tight text-ink flex items-center gap-2">
                    <Server className="w-5 h-5 text-ink/60" /> Tenants ({status?.tenant_count || 0})
                </h2>
                <div className="mt-4 border border-ink/10 bg-white" data-testid="db-admin-tenant-list">
                    {status?.tenants?.length === 0 && (
                        <div className="p-6 text-sm text-ink/40">No tenants registered yet.</div>
                    )}
                    <ul className="divide-y divide-ink/5">
                        {(status?.tenants || []).map((t) => {
                            const s = stats[t.company_id];
                            return (
                                <li key={t.company_id} className="p-5" data-testid={`db-admin-tenant-${t.company_id}`}>
                                    <div className="flex items-start justify-between gap-4 flex-wrap">
                                        <div className="min-w-0 flex-1">
                                            <div className="font-medium text-ink flex items-center gap-2">
                                                {t.company_id}
                                                {perTenant && !t.exists && (
                                                    <span className="inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] border border-red-300 bg-red-50 text-red-700" data-testid={`db-admin-missing-${t.company_id}`}>
                                                        Missing
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-ink/50 mt-0.5 font-mono">
                                                {t.database}{perTenant ? (t.exists ? " · exists" : " · MISSING") : " (legacy mode)"}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {perTenant && !t.exists && (
                                                <button
                                                    onClick={() => initTenant(t.company_id)}
                                                    disabled={!!working}
                                                    data-testid={`db-admin-init-${t.company_id}`}
                                                    className="inline-flex items-center gap-1 text-xs bg-red-600 text-white px-3 py-1.5 hover:bg-red-700"
                                                    title="Create the missing physical database and seed defaults"
                                                >
                                                    {working === `init-${t.company_id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlayCircle className="w-3 h-3" />}
                                                    Initialize DB
                                                </button>
                                            )}
                                            <button
                                                onClick={() => fetchStats(t.company_id)}
                                                disabled={!!working || statsLoading[t.company_id]}
                                                data-testid={`db-admin-stats-${t.company_id}`}
                                                className="inline-flex items-center gap-1 text-xs border border-ink/15 px-3 py-1.5 hover:bg-ink/5"
                                            >
                                                {statsLoading[t.company_id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
                                                Inspect
                                            </button>
                                            <button
                                                onClick={() => backup(t.company_id)}
                                                disabled={!!working}
                                                data-testid={`db-admin-backup-${t.company_id}`}
                                                className="inline-flex items-center gap-1 text-xs border border-klein text-klein px-3 py-1.5 hover:bg-klein hover:text-white"
                                                title="Take a point-in-time backup snapshot (non-destructive)"
                                            >
                                                {working === `backup-${t.company_id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                                Backup
                                            </button>
                                            <button
                                                onClick={() => restore(t.company_id)}
                                                disabled={!!working}
                                                data-testid={`db-admin-restore-${t.company_id}`}
                                                className="inline-flex items-center gap-1 text-xs border border-amber-500 text-amber-700 px-3 py-1.5 hover:bg-amber-500 hover:text-white"
                                                title="Restore from a backup snapshot (replaces current data)"
                                            >
                                                {working === `restore-${t.company_id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                                                Restore
                                            </button>
                                            <button
                                                onClick={() => rewrite(t.company_id, { dryRun: true })}
                                                disabled={!!working}
                                                data-testid={`db-admin-dryrun-${t.company_id}`}
                                                className="inline-flex items-center gap-1 text-xs border border-ink/15 px-3 py-1.5 hover:bg-ink/5"
                                            >
                                                {working === `rewrite-${t.company_id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wrench className="w-3 h-3" />}
                                                Preview repair
                                            </button>
                                            <button
                                                onClick={() => rewrite(t.company_id, { dryRun: false })}
                                                disabled={!!working}
                                                data-testid={`db-admin-rewrite-${t.company_id}`}
                                                className="inline-flex items-center gap-1 text-xs bg-ink text-white px-3 py-1.5 hover:bg-ink/90"
                                            >
                                                {working === `rewrite-${t.company_id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wrench className="w-3 h-3" />}
                                                Rewrite database
                                            </button>
                                        </div>
                                    </div>
                                    {s && (
                                        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                            <Stat label="Total docs" value={s.total_docs} />
                                            <Stat label="Collections" value={Object.keys(s.collection_counts || {}).length} />
                                            <Stat label="Employees" value={s.collection_counts?.employees ?? 0} />
                                            <Stat label="Orphans" value={countOrphans(s.orphans)} accent={countOrphans(s.orphans) > 0} />
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>

            {/* Backups */}
            <div className="mt-12">
                <h2 className="font-display font-bold text-xl tracking-tight text-ink flex items-center gap-2">
                    <Archive className="w-5 h-5 text-ink/60" /> Backups ({backups.length})
                </h2>
                <p className="mt-1 text-xs text-ink/50">
                    Every &ldquo;Rewrite database&rdquo; action snapshots the tenant DB into a timestamped backup first. Delete older snapshots once you&apos;ve verified the rewrite was successful.
                </p>
                <div className="mt-4 border border-ink/10 bg-white" data-testid="db-admin-backups">
                    {backups.length === 0 && <div className="p-6 text-sm text-ink/40">No backup databases on disk.</div>}
                    <ul className="divide-y divide-ink/5">
                        {backups.map((name) => (
                            <li key={name} className="px-5 py-3 flex items-center justify-between">
                                <span className="font-mono text-xs text-ink/70">{name}</span>
                                <button
                                    onClick={() => dropDb(name)}
                                    disabled={!!working}
                                    data-testid={`db-admin-drop-${name}`}
                                    className="inline-flex items-center gap-1 text-xs border border-red-200 text-red-700 px-3 py-1 hover:bg-red-50"
                                >
                                    {working === `drop-${name}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                                    Delete
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* Last report */}
            {report && (
                <div className="mt-12">
                    <h2 className="font-display font-bold text-xl tracking-tight text-ink flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Last operation report
                    </h2>
                    <pre className="mt-3 bg-ink/[0.03] border border-ink/10 p-4 text-[11px] leading-relaxed overflow-x-auto whitespace-pre-wrap" data-testid="db-admin-report">
{JSON.stringify(report, null, 2)}
                    </pre>
                </div>
            )}

            {/* Active migration job progress */}
            {activeJob && activeJob.status !== "complete" && activeJob.status !== "failed" && (
                <div className="mt-6 border border-klein/30 bg-klein/5 p-5" data-testid="db-admin-active-job">
                    <div className="flex items-center gap-3">
                        <Loader2 className="w-4 h-4 animate-spin text-klein" />
                        <div>
                            <div className="text-sm font-medium text-ink">Migration in progress — job {activeJob.id?.slice(0, 8)}</div>
                            <div className="text-xs text-ink/50 mt-0.5">Status: {activeJob.status}. This page will update automatically.</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Cleanup confirm dialog */}
            {confirmCleanup && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="db-admin-cleanup-dialog">
                    <div className="bg-white max-w-lg w-full border border-ink/10">
                        <div className="p-5 border-b border-ink/10 flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-red-700" />
                            <h3 className="font-display font-bold text-lg">Drop legacy data from central DB?</h3>
                        </div>
                        <div className="p-5 text-sm text-ink/80 space-y-3">
                            <p>Only run this <strong>after</strong> per-tenant migration has been verified healthy. This will:</p>
                            <ul className="list-disc pl-5 space-y-1 text-ink/70 text-sm">
                                <li>Drop every per-tenant collection from the central database (employees, leaves, loans, payroll_runs, payroll_items, attendance, documents, etc.).</li>
                                <li><strong>Keep</strong> the global collections (companies, users, system_state).</li>
                                <li>Permanently free up disk space — backups in <code className="font-mono text-xs">hrm_*_bak_*</code> databases remain available.</li>
                            </ul>
                            <p className="text-xs text-red-700"><strong>This cannot be undone.</strong> Make sure per-tenant DBs are healthy first.</p>
                        </div>
                        <div className="p-5 border-t border-ink/10 flex items-center justify-end gap-2 bg-ink/[0.02]">
                            <button onClick={() => setConfirmCleanup(false)} disabled={!!working} className="text-sm text-ink/60 hover:text-ink px-4 py-2">Cancel</button>
                            <button
                                onClick={cleanupLegacy}
                                disabled={!!working}
                                data-testid="db-admin-confirm-cleanup"
                                className="inline-flex items-center gap-2 bg-red-700 text-white px-4 py-2 text-sm hover:bg-red-800 disabled:opacity-60"
                            >
                                {working === "cleanup" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                {working === "cleanup" ? "Dropping…" : "Yes, drop legacy data"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirm migration dialog */}
            {confirmEnableMigration && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="db-admin-confirm-dialog">
                    <div className="bg-white max-w-lg w-full border border-ink/10">
                        <div className="p-5 border-b border-ink/10 flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-yellow-700" />
                            <h3 className="font-display font-bold text-lg">Confirm per-tenant DB migration</h3>
                        </div>
                        <div className="p-5 text-sm text-ink/80 space-y-3">
                            <p>This will:</p>
                            <ul className="list-disc pl-5 space-y-1 text-ink/70 text-sm">
                                <li>Copy every per-tenant document from the central DB into <code className="font-mono text-xs">hrm_&lt;company-id&gt;</code> databases.</li>
                                <li>Rebuild standard indexes on each tenant DB (no cross-tenant unique constraints).</li>
                                <li>Flip the runtime flag so all future reads/writes route per-tenant.</li>
                            </ul>
                            <p className="text-xs text-ink/50">The central DB is not modified. Migration is idempotent — safe to re-run.</p>
                        </div>
                        <div className="p-5 border-t border-ink/10 flex items-center justify-end gap-2 bg-ink/[0.02]">
                            <button onClick={() => setConfirmEnableMigration(false)} disabled={!!working} className="text-sm text-ink/60 hover:text-ink px-4 py-2">Cancel</button>
                            <button
                                onClick={enableMigration}
                                disabled={!!working}
                                data-testid="db-admin-confirm-migrate"
                                className="inline-flex items-center gap-2 bg-klein text-white px-4 py-2 text-sm hover:bg-klein/90 disabled:opacity-60"
                            >
                                {working === "enable" ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
                                {working === "enable" ? "Migrating…" : "Run migration"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, accent }) {
    return (
        <div className={`border border-ink/10 p-3 ${accent ? "bg-yellow-50 border-yellow-200" : "bg-white"}`}>
            <div className="overline">{label}</div>
            <div className={`mt-1 font-display font-bold text-lg tabular-nums ${accent ? "text-yellow-800" : "text-ink"}`}>{value}</div>
        </div>
    );
}

function countOrphans(orphans) {
    if (!orphans) return 0;
    let n = 0;
    for (const coll of Object.values(orphans)) {
        for (const v of Object.values(coll || {})) n += v;
    }
    return n;
}
